import { faArrowUpRightFromSquare, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import logoImg from 'public/static/icon-192.png';
import React, { useContext, useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip as BSTooltip } from 'react-bootstrap';
import TruncatedTitle from 'src/components/TruncatedTitle';
import FeaturesContext from 'src/context/FeaturesContext';
import useMediaQuery from 'src/hooks/useMediaQuery';
import { api } from 'src/utils/api';
import config from 'src/utils/config';
import { API_RESOURCES, BUREAU_BARCODE_FORMAT } from 'src/utils/constants';
import { FEATURES, isFeatureEnabled } from 'src/utils/features';
import { getShortUuid, getUuid } from 'src/utils/url';

import BarcodeQrCodeOutput from './BarcodeQrCodeOutput';
import Feature from './feature';
import IntlDate from './intl-date.jsx';
import IntlNumber from './intl-number.jsx';

const ModelPreview = ({ model, noModelUpload, isRicoh, isPowderWorkflow }) => (
  <>
    {!isPowderWorkflow && noModelUpload && (
      <div className='model hide-on-print'>
        <img src='/no_circle.png' alt='No' />
        <h5 style={{ color: 'gray' }}>Piece has No Model data</h5>
      </div>
    )}
    {model && (
      <div className={isRicoh ? 'ricoh-model' : 'model'}>
        <img src={model.snapshot_content} alt='' />
      </div>
    )}
  </>
);
/* eslint-disable camelcase */
ModelPreview.propTypes = {
  model: PropTypes.shape({
    snapshot_content: PropTypes.string,
  }),
  noModelUpload: PropTypes.bool,
  isRicoh: PropTypes.bool,
  isPowderWorkflow: PropTypes.bool,
};

const RicohQRSection = ({ model, noModelUpload, isRicoh, qrUrl, scanningMode }) => (
  <div className='d-flex flex-row align-items-center justify-content-center w-100 mb10'>
    <ModelPreview model={model} noModelUpload={noModelUpload} isRicoh={isRicoh} />
    <BarcodeQrCodeOutput
      isRicoh={isRicoh}
      url={qrUrl}
      scanningMode={scanningMode}
      className='ricoh-model'
    />
  </div>
);
/* eslint-disable camelcase */
RicohQRSection.propTypes = {
  model: PropTypes.shape({
    snapshot_content: PropTypes.string,
  }),
  noModelUpload: PropTypes.bool,
  isRicoh: PropTypes.bool,
  qrUrl: PropTypes.string,
  scanningMode: PropTypes.string,
};

const Traveler = ({
  print,
  run,
  order,
  lineItem,
  model,
  workflow,
  orderOwner,
  printingProcessStepRun,
  piece,
  isPowderWorkflow,
  scanningMode,
  shippingType,
  printerType,
  labelsByPiece,
  location,
  bureauBranding,
}) => {
  const [qrUrl, setQrUrl] = useState('');
  const [userLocale, setUserLocale] = useState('en-US');
  const [bureauLogoSrc, setBureauLogoSrc] = useState(null);
  const isMobileView = useMediaQuery('(min-width: 600px) and (max-width: 1024px)');
  const customFieldValue = lineItem?.custom_field_values[0]?.value;

  const { features } = useContext(FeaturesContext);
  const isAmazonUser = isFeatureEnabled(features, FEATURES.AMAZON_ORDER_FIELDS);
  const isQRPrintsTraveler = isFeatureEnabled(features, FEATURES.QR_PRINTS_TRAVELER);
  const isRicohUSAOrderFields = isFeatureEnabled(features, FEATURES.RICOH_USA_ORDER_FIELDS);

  const shouldHideModel = isMobileView && isQRPrintsTraveler && isRicohUSAOrderFields;
  const isRicohPrintLayout = isQRPrintsTraveler && isRicohUSAOrderFields;

  useEffect(() => {
    if (piece?.uri) {
      const url = new URL(`${window.location.origin}/summary`);
      url.searchParams.set('scannedPiece', getUuid(piece.uri));
      setQrUrl(String(url));
    }
  }, [piece?.uri]);

  useEffect(() => {
    api
      .get(`${API_RESOURCES.BUREAU_SETTINGS}/`)
      .json()
      .then(result => {
        setUserLocale(result.resources[0].default_currency === 'USD' ? 'en-US' : 'en-GB');
      })
      .catch(error => {
        setUserLocale('en-US');
        console.error(error);
      });
  }, []);

  useEffect(() => {
    const resolveBureauLogo = async () => {
      const logoUri = bureauBranding?.printable_logo || bureauBranding?.logo || null;
      if (!logoUri) {
        setBureauLogoSrc(null);
        return;
      }

      if (logoUri.includes(`/${API_RESOURCES.DOCUMENT}/`)) {
        try {
          const document = await api.get(`${API_RESOURCES.DOCUMENT}/${getUuid(logoUri)}/`).json();
          setBureauLogoSrc(document?.upload_location || logoUri);
          return;
        } catch (error) {
          console.error(error);
        }
      }

      setBureauLogoSrc(logoUri);
    };

    resolveBureauLogo();
  }, [bureauBranding]);

  let formattedDate = 'N/A';
  let orderUrl = null;
  let pieceUrl = null;

  if (order) {
    const { due_date: dueDate, uri: orderUri } = order;

    if (dueDate) {
      formattedDate = <IntlDate date={dueDate} locale={userLocale} />;
    }

    if (isAmazonUser) {
      orderUrl = `${config.rapidfabHost}/#/records/order/${getUuid(orderUri)}`;
    }
  }
  if (piece && isAmazonUser) {
    pieceUrl = `${config.rapidfabHost}/#/records/piece/${getUuid(piece.uri)}`;
  }

  return (
    <div className={clsx('traveler', isRicohPrintLayout && 'traveler--ricoh-print')}>
      {!shouldHideModel && (
        <ModelPreview
          model={model}
          noModelUpload={lineItem?.additive?.no_model_upload}
          isPowderWorkflow={isPowderWorkflow}
        />
      )}
      <Feature
        featureName={FEATURES.QR_PRINTS_TRAVELER}
        excludeFeatures={[FEATURES.RICOH_USA_ORDER_FIELDS]}
      >
        <div className='print-info'>
          <h1>
            {(piece && (
              <TruncatedTitle
                inline
                title={piece.name}
                maxTextLength={25}
                maxTextLengthMobile={20}
                tooltipPlacement='bottom'
              />
            )) ||
              'N/A'}{' '}
            {lineItem?.customer_id && `(${lineItem.customer_id})`}
            {pieceUrl && (
              <a href={pieceUrl} className='spacer-left' target='_blank' rel='noreferrer'>
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              </a>
            )}
          </h1>
          <dl>
            <div>
              <Feature featureName={FEATURES.AMPD_LABS_ORDER_FIELDS}>
                <div className='d-flex'>
                  <dt className='me-2 mr-2 fw-bold'>Piece ID:</dt>
                  <dd>{(piece && getShortUuid(piece.uri)) || 'N/A'}</dd>
                </div>
                <div className='d-flex'>
                  <dt className='me-2 mr-2 fw-bold'>Run ID:</dt>
                  <dd>
                    {(run?.customer_id && run.customer_id.replace(/\..*$/, '')) ||
                      (run?.uri && getShortUuid(run.uri)) ||
                      'N/A'}
                  </dd>
                </div>
              </Feature>
              <Feature isInverted featureName={FEATURES.POWDER_WORKFLOW}>
                <div className='d-flex'>
                  <dt className='me-2 mr-2 fw-bold'>Material:</dt>
                  <dd>{print.material_name}</dd>
                </div>
              </Feature>
              <div className='d-flex'>
                <dt className='me-2 mr-2 fw-bold'>Workflow:</dt>
                <dd className='mb-0'>{workflow ? workflow.name : 'N/A'}</dd>
              </div>
              <div className='d-flex'>
                <dt className='me-2 mr-2 fw-bold'>Quantity:</dt>
                <dd>
                  Piece <IntlNumber number={print.copy} /> of{' '}
                  {lineItem ? <IntlNumber number={lineItem.quantity} /> : 'N/A'}
                </dd>
              </div>
              <div className='d-flex'>
                <dt className='me-2 mr-2 fw-bold'>Order:</dt>
                <dd>
                  {order ? `${order.name}${order.customer_id && ` (${order.customer_id})`}` : 'N/A'}
                  {orderUrl && (
                    <a href={orderUrl} className='spacer-left' target='_blank' rel='noreferrer'>
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    </a>
                  )}
                </dd>
              </div>
              <Feature isInverted featureName={FEATURES.AMPD_LABS_ORDER_FIELDS}>
                <div className='d-flex'>
                  <dt className='me-2 mr-2 fw-bold'>Customer:</dt>
                  <dd>
                    {(order && order.customer_name) || 'N/A'}{' '}
                    {order?.customer_po && `(${order.customer_po})`}
                  </dd>
                </div>
              </Feature>
              <Feature featureName={FEATURES.TRIMECH_ORDER_FIELDS}>
                <div className='d-flex'>
                  <dt className='me-2 mr-2 fw-bold'>Shipping Type:</dt>
                  <dd>{shippingType?.name || 'N/A'}</dd>
                </div>
              </Feature>
              <Feature isInverted featureName={FEATURES.AMPD_LABS_ORDER_FIELDS}>
                <div className='d-flex'>
                  <dt className='me-2 mr-2 fw-bold'>Shipping:</dt>
                  <dd>{(order && order.shipping.address) || 'N/A'}</dd>
                </div>
              </Feature>
              <Feature featureName={FEATURES.DUE_DATE}>
                <div className='d-flex'>
                  <dt className='me-2 mr-2 fw-bold'>Due Date:</dt>
                  <dd>{formattedDate}</dd>
                </div>
              </Feature>
              <Feature featureName={FEATURES.TRIMECH_ORDER_FIELDS}>
                <div className='d-flex'>
                  <dt className='me-2 mr-2 fw-bold'>Line Item Notes:</dt>
                  <dd>
                    <TruncatedTitle
                      title={lineItem?.notes || 'N/A'}
                      maxTextLength={100}
                      maxTextLengthMobile={100}
                    />
                  </dd>
                </div>
                {printerType && (
                  <div className='d-flex'>
                    <dt className='me-2 mr-2 fw-bold'>Layer Thickness:</dt>
                    <dd>
                      {lineItem?.additive?.layer_thickness}
                      {printerType.layer_thickness &&
                        printerType.layer_thickness !== lineItem?.additive?.layer_thickness && (
                          <OverlayTrigger
                            placement='top'
                            overlay={
                              <BSTooltip>
                                The Layer Height has a different layer thickness then Printer
                                Default ({printerType.layer_thickness}).
                              </BSTooltip>
                            }
                          >
                            <FontAwesomeIcon
                              icon={faTriangleExclamation}
                              color='#FFC008'
                              className='spacer-left'
                            />
                          </OverlayTrigger>
                        )}
                    </dd>
                  </div>
                )}
              </Feature>
            </div>
          </dl>
        </div>
      </Feature>

      <Feature
        featureNamesEnforceAll
        featureNames={[FEATURES.QR_PRINTS_TRAVELER, FEATURES.RICOH_USA_ORDER_FIELDS]}
      >
        <div className='ricoh-print-info'>
          {shouldHideModel && (
            <RicohQRSection
              isRicoh
              model={model}
              noModelUpload={lineItem?.additive?.no_model_upload}
              qrUrl={qrUrl}
              scanningMode={scanningMode}
            />
          )}

          <h2>{piece && piece.customer_id ? `(${piece.customer_id})` : 'N/A'}</h2>
          <dl>
            <dt>Part Name:</dt>
            <dd>{(lineItem && lineItem.name) || 'N/A'}</dd>
            <dt>File Name:</dt>
            <dd>{(model && model.name) || 'N/A'}</dd>
            <dt>Workflow:</dt>
            <dd>{workflow ? workflow.name : 'N/A'}</dd>
            <dt>Quantity:</dt>
            <dd>
              {' '}
              Piece <IntlNumber number={print.copy} /> of{' '}
              {lineItem ? <IntlNumber number={lineItem.quantity} /> : 'N/A'}{' '}
            </dd>
            <dt>Label:</dt>
            <dd className='workflow-labels'>
              {labelsByPiece && Object.values(labelsByPiece).length > 0
                ? Object.values(labelsByPiece)
                    .flat()
                    .map(label => (
                      <div key={label.id} className='workflow-label'>
                        {label.color && (
                          <span
                            className='ricoh-small-status-dot spacer-right'
                            style={{ backgroundColor: label.color }}
                          />
                        )}
                        {label.name}
                      </div>
                    ))
                : 'N/A'}
            </dd>
            <div className='mb10' />
            <hr />
            <dt>Printer Name:</dt>
            <dd>{printingProcessStepRun?.workstation_name || 'N/A'}</dd>
            <dt>Material:</dt>
            <dd>{(piece && piece.material_name) || 'N/A'}</dd>
            <dt>Order:</dt>
            <dd>
              {' '}
              {order
                ? `${order.name} (${order.customer_id || getShortUuid(order.uri)})`
                : 'N/A'}{' '}
            </dd>
            <dt>Location:</dt>
            <dd>{(location && location.name) || 'N/A'}</dd>
            <dt>Customer:</dt>
            <dd>{(order && order.customer_name) || 'N/A'}</dd>
            <dt>Created:</dt>
            <dd>{order ? <IntlDate date={order.created} locale={userLocale} /> : 'N/A'}</dd>
            <dt>Due Date:</dt>
            <dd>{formattedDate}</dd>
            <dt>Line Item Notes:</dt>
            <dd className='truncate-3-lines'>{(lineItem && lineItem.notes) || 'N/A'}</dd>
          </dl>
          <h4 className='mt25'>NC information (Fill out per applicable WI)</h4>
          <table className='ricoh-table'>
            <thead>
              <tr>
                <th className='cell'>
                  <strong>Workstation</strong>
                  <br />
                  (Name and EQP)
                  <br />
                  where failure mode observed
                </th>
                <th className='cell'>
                  <strong>
                    Qty of pieces
                    <br />
                    removed for
                    <br />
                    quarantine
                  </strong>
                </th>
                <th className='cell'>
                  <strong>
                    Qty Total
                    <br />
                    available pieces
                  </strong>
                </th>
                <th className='cell cell-active'>
                  <strong>Active Total</strong>
                </th>
                <th className='cell'>
                  <strong>Initial and Date</strong>
                </th>
              </tr>
            </thead>
            <tbody>
              {[...Array(3)].map((_, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={idx}>
                  <td className='cell body-cell' />
                  <td className='cell body-cell' />
                  <td className='cell body-cell' />
                  <td className='cell body-cell cell-active' />
                  <td className='cell body-cell' />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Feature>

      <Feature
        isInverted
        featureNames={[FEATURES.BEEHIVE3D_ORDER_FIELDS, FEATURES.QR_PRINTS_TRAVELER]}
      >
        <div className='print-info'>
          <h1>
            {order ? `${order.name} (${order.customer_id || getShortUuid(order.uri)})` : 'N/A'}
          </h1>
          <dl>
            <div>
              <Feature featureName={FEATURES.BOEING_ORDER_FIELDS}>
                <div className='d-flex'>
                  <dt className='me-2 mr-2 fw-bold text-left'>Part Name:</dt>
                  <dd>{(lineItem && lineItem.name) || 'N/A'}</dd>
                </div>
              </Feature>
              <div className='d-flex'>
                <dt className='me-2 mr-2 fw-bold text-left'>Customer:</dt>
                <dd>{(order && order.customer_name) || 'N/A'}</dd>
              </div>

              <Feature featureName={FEATURES.SINTAVIA_ORDER_FIELDS}>
                <div className='d-flex'>
                  <dt className='me-2 mr-2 fw-bold text-left'>Owner:</dt>
                  <dd>{orderOwner ? `${orderOwner.name} (${orderOwner.username})` : 'N/A'}</dd>
                </div>

                <div className='d-flex'>
                  <dt className='me-2 mr-2 fw-bold text-left'>Lot:</dt>
                  <dd>{printingProcessStepRun ? printingProcessStepRun.name : 'N/A'}</dd>
                </div>
              </Feature>

              <Feature featureName={FEATURES.DUE_DATE}>
                <div className='d-flex'>
                  <dt className='me-2 mr-2 fw-bold text-left'>Due Date:</dt>
                  <dd>{formattedDate}</dd>
                </div>
              </Feature>

              <div className='d-flex'>
                <dt className='me-2 mr-2 fw-bold text-left'>Shipping:</dt>
                <dd>{(order && order.shipping.address) || 'N/A'}</dd>
              </div>

              <Feature isInverted featureName={FEATURES.POWDER_WORKFLOW}>
                <div className='me-2 mr-2 fw-bold d-flex'>
                  <dt className='text-left'>Material:</dt>
                  <dd>{print.material_name || 'N/A'}</dd>
                </div>
              </Feature>
              <div className='d-flex'>
                <dt className='me-2 mr-2 fw-bold text-left'>Workflow:</dt>
                <dd>{workflow ? workflow.name : 'N/A'}</dd>
              </div>

              <div className='d-flex'>
                <dt className='me-2 mr-2 fw-bold text-left'>Quantity:</dt>
                <dd>
                  <IntlNumber number={print.copy} /> /{' '}
                  {lineItem ? <IntlNumber number={lineItem.quantity} /> : 'N/A'}
                </dd>
              </div>
            </div>
          </dl>
        </div>
      </Feature>
      <Feature
        excludeFeatures={[FEATURES.QR_PRINTS_TRAVELER]}
        featureName={FEATURES.RICOH_USA_ORDER_FIELDS}
      >
        <div className='print-info'>
          <h1>{piece && piece.customer_id ? `(${piece.customer_id})` : 'N/A'}</h1>
          <dl>
            <div>
              <div className='d-flex'>
                <dt className='me-2 mr-2 fw-bold text-left'>Part:</dt>
                <dd>
                  {(lineItem && lineItem.customer_id) || 'N/A'} (Revision:{' '}
                  {customFieldValue || 'N/A'})
                </dd>
              </div>

              <Feature isInverted featureName={FEATURES.POWDER_WORKFLOW}>
                <div className='d-flex'>
                  <dt className='me-2 mr-2 fw-bold text-left'>Material:</dt>
                  <dd>{print.material_name}</dd>
                </div>
              </Feature>
              <hr className='hide-on-print' />
              <div className='d-flex'>
                <dt className='me-2 mr-2 fw-bold text-left'>Order:</dt>
                <dd>
                  {order
                    ? `${order.name} (${order.customer_id || getShortUuid(order.uri)})`
                    : 'N/A'}
                </dd>
              </div>

              <div className='d-flex'>
                <dt className='me-2 mr-2 fw-bold text-left'>Customer:</dt>
                <dd>{(order && order.customer_name) || 'N/A'}</dd>
              </div>

              <div className='d-flex'>
                <dt className='me-2 mr-2 fw-bold text-left'>PO Number:</dt>
                <dd>{(order && order.customer_po) || 'N/A'}</dd>
              </div>

              <Feature featureName={FEATURES.DUE_DATE}>
                <div className='d-flex'>
                  <dt className='me-2 mr-2 fw-bold text-left'>Due Date:</dt>
                  <dd>{formattedDate}</dd>
                </div>
              </Feature>

              <div className='d-flex'>
                <dt className='me-2 mr-2 fw-bold text-left'>Owner:</dt>
                <dd>{(order && order.order_owner_name) || 'N/A'}</dd>
              </div>
            </div>
          </dl>
        </div>
      </Feature>

      {shouldHideModel ? null : <BarcodeQrCodeOutput url={qrUrl} scanningMode={scanningMode} />}

      <Feature
        isInverted
        featureName={FEATURES.BEEHIVE3D_ORDER_FIELDS}
        excludeFeatures={[FEATURES.RICOH_USA_ORDER_FIELDS]}
      >
        <footer>
          {bureauLogoSrc ? (
            <p className='bureau-logo-footer'>
              <img className='custom-logo' src={bureauLogoSrc} alt='Bureau Logo' />
            </p>
          ) : (
            <p>
              Powered by <img className='logo' src={logoImg} alt='' /> Authentise
            </p>
          )}
        </footer>
      </Feature>
    </div>
  );
};

// API results are camelcase ¯\_(ツ)_/¯
/* eslint-disable camelcase */
Traveler.propTypes = {
  print: PropTypes.shape({
    uri: PropTypes.string.isRequired,
    copy: PropTypes.number.isRequired,
    material_name: PropTypes.string,
    run: PropTypes.string,
  }).isRequired,
  run: PropTypes.shape({
    customer_id: PropTypes.string,
    uri: PropTypes.string.isRequired,
  }).isRequired,
  order: PropTypes.shape({
    due_date: PropTypes.string,
    customer_name: PropTypes.string,
    name: PropTypes.string.isRequired,
    uri: PropTypes.string.isRequired,
    shipping: PropTypes.shape({
      address: PropTypes.string,
    }),
    customer_id: PropTypes.string,
    created: PropTypes.string.isRequired,
    order_owner_name: PropTypes.string,
    customer_po: PropTypes.string,
    location: PropTypes.string,
  }),
  lineItem: PropTypes.shape({
    additive: PropTypes.shape({
      no_model_upload: PropTypes.bool,
      layer_thickness: PropTypes.number.isRequired,
    }).isRequired,
    quantity: PropTypes.number.isRequired,
    uri: PropTypes.string.isRequired,
    name: PropTypes.string,
    custom_field_values: PropTypes.string,
    customer_id: PropTypes.string,
    notes: PropTypes.string,
  }),
  model: PropTypes.shape({
    snapshot_content: PropTypes.string.isRequired,
    name: PropTypes.string,
  }),
  workflow: PropTypes.shape({
    name: PropTypes.string,
    labels: PropTypes.shape([]),
  }),
  orderOwner: PropTypes.shape({
    name: PropTypes.string.isRequired,
    username: PropTypes.string.isRequired,
  }),
  printingProcessStepRun: PropTypes.shape({
    name: PropTypes.string.isRequired,
    workstation_name: PropTypes.string.isRequired,
  }),
  piece: PropTypes.shape({
    customer_id: PropTypes.string,
    name: PropTypes.string,
    uri: PropTypes.string,
    material_name: PropTypes.string,
  }),
  isPowderWorkflow: PropTypes.bool,
  scanningMode: PropTypes.string,
  shippingType: PropTypes.shape({
    name: PropTypes.string,
  }),
  printerType: PropTypes.shape({
    layer_thickness: PropTypes.number,
  }),
  labelsByPiece: PropTypes.objectOf(
    PropTypes.shape({
      uri: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  location: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }),
  bureauBranding: PropTypes.shape({
    logo: PropTypes.string,
    printable_logo: PropTypes.string,
  }),
};

Traveler.defaultProps = {
  orderOwner: null,
  printingProcessStepRun: null,

  // Next empty values added to cover some edge-cases with deleted entities
  // which should not be usually accessed in real life examples

  // Order may already be deleted
  order: null,
  // Model may be null, when line item is already deleted
  model: null,
  // Workflow may be null, when line item is already deleted
  workflow: null,
  // Line Item may already be deleted
  lineItem: null,
  piece: null,
  isPowderWorkflow: false,
  scanningMode: BUREAU_BARCODE_FORMAT.QR,
  shippingType: null,
  printerType: null,
  labelsByPiece: null,
  bureauBranding: null,
};

/* eslint-enable camelcase */

export default Traveler;
